// Isometric 3-D roof illustrations — premium architectural style.
// Warm terracotta roofs, charcoal houses, soft ground shadows,
// gradient shading for depth, subtle white edge highlights.
import React from "react";

/* Shared <defs> rendered once. Uses globally-unique IDs prefixed with `rqp-`. */
export const SharedDefs = () => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
    <defs>
      {/* Roof faces — warm terracotta sunlight to shadow */}
      <linearGradient id="rqp-r-lit" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#F08A6E" />
        <stop offset="1" stopColor="#C24A2D" />
      </linearGradient>
      <linearGradient id="rqp-r-mid" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#C24A2D" />
        <stop offset="1" stopColor="#8A2D17" />
      </linearGradient>
      <linearGradient id="rqp-r-shade" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#5B1B0F" />
        <stop offset="1" stopColor="#2E0B05" />
      </linearGradient>
      {/* Walls — warm charcoal */}
      <linearGradient id="rqp-w-lit" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#3D4655" />
        <stop offset="1" stopColor="#222A38" />
      </linearGradient>
      <linearGradient id="rqp-w-shade" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#1B2230" />
        <stop offset="1" stopColor="#0B1018" />
      </linearGradient>
      <linearGradient id="rqp-w-dark" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#0B1018" />
        <stop offset="1" stopColor="#000000" />
      </linearGradient>
      <radialGradient id="rqp-shadow" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor="#000000" stopOpacity="0.55" />
        <stop offset="1" stopColor="#000000" stopOpacity="0" />
      </radialGradient>
    </defs>
  </svg>
);

const Frame = ({ children, title }) => (
  <svg viewBox="0 0 200 140" className="w-full h-full block" preserveAspectRatio="xMidYMid meet" aria-label={title}>
    <ellipse cx="100" cy="128" rx="68" ry="5" fill="url(#rqp-shadow)" />
    {children}
  </svg>
);

const F = ({ d, fill, sw = 0.7, op = 0.55 }) => (
  <path d={d} fill={`url(#${fill})`} stroke="#FFFFFF" strokeOpacity={op} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round" />
);
const Edge = ({ d, w = 1.1 }) => (
  <path d={d} stroke="#FFFFFF" strokeWidth={w} fill="none" strokeLinecap="round" opacity="0.95" />
);

/* --- 16 roof types --- */

const Gable = () => (
  <Frame title="Gable">
    <F d="M60,80 L100,60 L140,80 L140,110 L100,90 L60,110 Z" fill="rqp-w-shade" />
    <F d="M60,110 L100,90 L140,110 L140,125 L100,110 L60,125 Z" fill="rqp-w-lit" />
    <F d="M60,80 L100,40 L100,55 Z" fill="rqp-r-shade" />
    <F d="M140,80 L100,40 L100,55 Z" fill="rqp-r-shade" />
    <F d="M60,80 L100,40 L140,80 L100,90 Z" fill="rqp-r-lit" />
    <Edge d="M100,40 L100,55" />
    <Edge d="M60,80 L100,40 L140,80" w={0.7} />
  </Frame>
);

const HipToHip = () => (
  <Frame title="Hip to Hip">
    <F d="M40,90 L100,70 L160,90 L160,115 L100,100 L40,115 Z" fill="rqp-w-lit" />
    <F d="M40,90 L80,50 L120,50 L160,90 L100,75 Z" fill="rqp-r-shade" />
    <F d="M40,90 L80,50 L120,50 L160,90 L100,100 Z" fill="rqp-r-lit" />
    <F d="M40,90 L80,50 L100,75 Z" fill="rqp-r-mid" />
    <F d="M160,90 L120,50 L100,75 Z" fill="rqp-r-mid" />
    <Edge d="M80,50 L120,50" />
    <Edge d="M40,90 L80,50" w={0.7} />
    <Edge d="M160,90 L120,50" w={0.7} />
  </Frame>
);

const Mono = () => (
  <Frame title="Mono Pitch">
    <F d="M50,105 L160,90 L160,120 L50,125 Z" fill="rqp-w-lit" />
    <F d="M40,55 L50,105 L50,125 L40,80 Z" fill="rqp-w-shade" />
    <F d="M40,55 L150,40 L160,90 L50,105 Z" fill="rqp-r-lit" />
    <Edge d="M40,55 L150,40" w={0.8} />
    <Edge d="M150,40 L160,90" w={0.6} />
  </Frame>
);

const LeanTo = () => (
  <Frame title="Lean-to">
    <F d="M40,40 L80,40 L80,115 L40,115 Z" fill="rqp-w-lit" />
    <F d="M80,40 L80,115 L96,108 L96,32 Z" fill="rqp-w-shade" />
    <F d="M40,40 L80,40 L96,32 L56,32 Z" fill="rqp-r-shade" />
    <F d="M80,75 L160,90 L160,115 L80,115 Z" fill="rqp-w-lit" />
    <F d="M160,90 L168,84 L168,107 L160,115 Z" fill="rqp-w-shade" />
    <F d="M80,55 L150,55 L160,90 L80,75 Z" fill="rqp-r-lit" />
    <F d="M150,55 L160,90 L168,84 L158,49 Z" fill="rqp-r-mid" />
    <F d="M80,55 L150,55 L158,49 L88,49 Z" fill="rqp-r-shade" />
    <Edge d="M80,55 L150,55" />
  </Frame>
);

const Flat = () => (
  <Frame title="Flat Roof">
    <F d="M40,80 L130,80 L130,115 L40,115 Z" fill="rqp-w-lit" />
    <F d="M130,80 L160,68 L160,103 L130,115 Z" fill="rqp-w-shade" />
    <F d="M40,80 L70,68 L160,68 L130,80 Z" fill="rqp-r-mid" />
    <Edge d="M40,80 L70,68 L160,68" w={0.8} />
  </Frame>
);

const LShaped = () => (
  <Frame title="L-Shaped">
    <F d="M30,95 L100,95 L100,120 L30,120 Z" fill="rqp-w-lit" />
    <F d="M100,75 L100,120 L120,108 L120,63 Z" fill="rqp-w-shade" />
    <F d="M100,95 L165,95 L165,120 L100,120 Z" fill="rqp-w-lit" />
    <F d="M165,95 L185,83 L185,108 L165,120 Z" fill="rqp-w-shade" />
    <F d="M30,95 L65,55 L100,55 L100,75 L65,75 Z" fill="rqp-r-lit" />
    <F d="M65,55 L80,45 L115,45 L100,55 Z" fill="rqp-r-shade" />
    <F d="M100,55 L115,45 L115,65 L100,75 Z" fill="rqp-r-mid" />
    <F d="M100,95 L130,55 L185,63 L165,95 Z" fill="rqp-r-lit" />
    <F d="M130,55 L185,63 L195,55 L140,47 Z" fill="rqp-r-shade" />
    <Edge d="M65,55 L100,55 L100,75" />
    <Edge d="M100,55 L130,55 L185,63" w={0.7} />
  </Frame>
);

const TShaped = () => (
  <Frame title="T-Shaped">
    <F d="M30,95 L170,95 L170,120 L30,120 Z" fill="rqp-w-lit" />
    <F d="M170,95 L185,87 L185,112 L170,120 Z" fill="rqp-w-shade" />
    <F d="M85,75 L115,75 L115,95 L85,95 Z" fill="rqp-w-shade" />
    <F d="M30,95 L60,65 L170,65 L140,95 Z" fill="rqp-r-lit" />
    <F d="M60,65 L170,65 L185,57 L75,57 Z" fill="rqp-r-shade" />
    <F d="M85,75 L100,50 L115,50 L115,75 Z" fill="rqp-r-mid" />
    <F d="M85,75 L100,50 L100,65 Z" fill="rqp-r-shade" />
    <Edge d="M60,65 L170,65" />
    <Edge d="M100,50 L100,65" />
  </Frame>
);

const Intersecting = () => (
  <Frame title="Intersecting">
    <F d="M30,95 L170,95 L170,120 L30,120 Z" fill="rqp-w-lit" />
    <F d="M170,95 L185,87 L185,112 L170,120 Z" fill="rqp-w-shade" />
    <F d="M30,95 L70,55 L170,55 L130,95 Z" fill="rqp-r-lit" />
    <F d="M70,55 L170,55 L185,47 L85,47 Z" fill="rqp-r-shade" />
    <F d="M80,90 L110,35 L150,35 L120,90 Z" fill="rqp-r-mid" />
    <F d="M110,35 L150,35 L165,27 L125,27 Z" fill="rqp-r-shade" />
    <Edge d="M70,55 L170,55" />
    <Edge d="M110,35 L150,35" />
  </Frame>
);

const Dormer = () => (
  <Frame title="Dormer">
    <F d="M30,95 L170,95 L170,120 L30,120 Z" fill="rqp-w-lit" />
    <F d="M170,95 L185,87 L185,112 L170,120 Z" fill="rqp-w-shade" />
    <F d="M30,95 L100,40 L170,95 Z" fill="rqp-r-lit" />
    <F d="M82,82 L82,62 L118,62 L118,82 Z" fill="rqp-w-shade" />
    <F d="M82,62 L100,50 L118,62 L100,72 Z" fill="rqp-r-mid" />
    <F d="M82,62 L100,50 L100,72 Z" fill="rqp-r-shade" />
    <Edge d="M30,95 L100,40 L170,95" w={0.8} />
    <Edge d="M100,50 L100,72" />
  </Frame>
);

const Mansard = () => (
  <Frame title="Mansard">
    <F d="M30,100 L170,100 L170,120 L30,120 Z" fill="rqp-w-lit" />
    <F d="M170,100 L185,92 L185,112 L170,120 Z" fill="rqp-w-shade" />
    <F d="M30,100 L60,72 L140,72 L170,100 Z" fill="rqp-r-lit" />
    <F d="M60,72 L140,72 L155,64 L75,64 Z" fill="rqp-r-shade" />
    <F d="M60,72 L90,52 L110,52 L140,72 Z" fill="rqp-r-mid" />
    <F d="M140,72 L110,52 L125,44 L155,64 Z" fill="rqp-r-shade" />
    <Edge d="M60,72 L140,72" />
    <Edge d="M90,52 L110,52" />
  </Frame>
);

const DutchBarn = () => (
  <Frame title="Dutch Barn">
    <F d="M40,100 L160,100 L160,120 L40,120 Z" fill="rqp-w-lit" />
    <F d="M160,100 L175,92 L175,112 L160,120 Z" fill="rqp-w-shade" />
    <F d="M40,100 L60,75 L140,75 L160,100 Z" fill="rqp-r-lit" />
    <F d="M60,75 L140,75 L155,67 L75,67 Z" fill="rqp-r-shade" />
    <F d="M60,75 L100,50 L140,75 Z" fill="rqp-r-mid" />
    <F d="M100,50 L140,75 L155,67 L115,42 Z" fill="rqp-r-shade" />
    <Edge d="M60,75 L140,75" />
    <Edge d="M100,50 L115,42" w={0.6} />
  </Frame>
);

const UnequalPitch = () => (
  <Frame title="Unequal Pitch">
    <F d="M40,95 L160,95 L160,120 L40,120 Z" fill="rqp-w-lit" />
    <F d="M160,95 L175,87 L175,112 L160,120 Z" fill="rqp-w-shade" />
    <F d="M40,95 L70,40 L70,55 L48,95 Z" fill="rqp-r-shade" />
    <F d="M70,40 L160,95 L48,95 Z" fill="rqp-r-lit" />
    <F d="M70,40 L160,95 L175,87 L85,32 Z" fill="rqp-r-mid" />
    <Edge d="M70,40 L160,95" w={0.7} />
    <Edge d="M70,40 L85,32" w={0.5} />
  </Frame>
);

const ValleyRoof = () => (
  <Frame title="Valley Roof">
    <F d="M30,95 L170,95 L170,120 L30,120 Z" fill="rqp-w-lit" />
    <F d="M170,95 L185,87 L185,112 L170,120 Z" fill="rqp-w-shade" />
    <F d="M30,95 L70,50 L70,65 L40,95 Z" fill="rqp-r-shade" />
    <F d="M30,95 L70,50 L100,80 L70,95 Z" fill="rqp-r-lit" />
    <F d="M170,95 L130,50 L130,65 L160,95 Z" fill="rqp-r-shade" />
    <F d="M170,95 L130,50 L100,80 L130,95 Z" fill="rqp-r-lit" />
    <F d="M70,50 L100,80 L130,50 L85,42 Z" fill="rqp-r-mid" />
    <Edge d="M70,50 L100,80 L130,50" />
    <Edge d="M70,50 L85,42 L130,50" w={0.5} />
  </Frame>
);

const Parapet = () => (
  <Frame title="Parapet">
    <F d="M40,95 L160,95 L160,120 L40,120 Z" fill="rqp-w-lit" />
    <F d="M160,95 L175,87 L175,112 L160,120 Z" fill="rqp-w-shade" />
    <F d="M30,90 L160,90 L160,55 L30,55 Z" fill="rqp-w-shade" />
    <F d="M160,90 L175,82 L175,47 L160,55 Z" fill="rqp-w-dark" />
    <F d="M30,55 L60,42 L175,42 L160,55 Z" fill="rqp-w-lit" />
    <F d="M48,68 L150,68 L150,82 L48,82 Z" fill="rqp-r-mid" />
    <Edge d="M30,55 L60,42 L175,42" w={0.7} />
    <Edge d="M160,55 L175,42" w={0.5} />
  </Frame>
);

const SteppedTerraces = () => (
  <Frame title="Stepped Terraces">
    <F d="M30,115 L80,115 L80,90 L30,90 Z" fill="rqp-w-lit" />
    <F d="M80,115 L92,107 L92,82 L80,90 Z" fill="rqp-w-shade" />
    <F d="M30,90 L42,82 L92,82 L80,90 Z" fill="rqp-r-mid" />
    <F d="M80,115 L130,115 L130,75 L80,75 Z" fill="rqp-w-lit" />
    <F d="M130,115 L142,107 L142,67 L130,75 Z" fill="rqp-w-shade" />
    <F d="M80,75 L92,67 L142,67 L130,75 Z" fill="rqp-r-mid" />
    <F d="M130,115 L175,115 L175,55 L130,55 Z" fill="rqp-w-lit" />
    <F d="M175,115 L185,107 L185,47 L175,55 Z" fill="rqp-w-shade" />
    <F d="M130,55 L140,47 L185,47 L175,55 Z" fill="rqp-r-mid" />
    <Edge d="M30,90 L80,90 L80,75 L130,75 L130,55 L175,55" w={0.7} />
  </Frame>
);

const ComplexHipsValleys = () => (
  <Frame title="Complex Hips & Valleys">
    <F d="M25,100 L175,100 L175,120 L25,120 Z" fill="rqp-w-lit" />
    <F d="M175,100 L188,92 L188,112 L175,120 Z" fill="rqp-w-shade" />
    <F d="M25,100 L65,55 L135,55 L175,100 Z" fill="rqp-r-lit" />
    <F d="M65,55 L135,55 L150,47 L80,47 Z" fill="rqp-r-shade" />
    <F d="M25,100 L65,55 L80,47 L40,92 Z" fill="rqp-r-mid" />
    <F d="M175,100 L135,55 L150,47 L188,92 Z" fill="rqp-r-mid" />
    <F d="M70,95 L100,62 L130,95 Z" fill="rqp-r-mid" />
    <F d="M70,95 L100,62 L100,82 Z" fill="rqp-r-shade" />
    <Edge d="M65,55 L135,55" />
    <Edge d="M100,62 L100,82" />
  </Frame>
);

const ISO_BY_ID = {
  "gable-to-gable": Gable,
  "hip-to-hip": HipToHip,
  "mono-pitch": Mono,
  "lean-to": LeanTo,
  flat: Flat,
  "l-shaped": LShaped,
  "t-shaped": TShaped,
  intersecting: Intersecting,
  dormer: Dormer,
  mansard: Mansard,
  "dutch-barn": DutchBarn,
  "unequal-pitch": UnequalPitch,
  valley: ValleyRoof,
  parapet: Parapet,
  "stepped-terraces": SteppedTerraces,
  complex: ComplexHipsValleys,
};

export function IsoRoof({ id }) {
  return (
    <img
      src={`/roof-images/${id}.png`}
      alt={id}
      className="w-full h-full object-contain"
      loading="lazy"
      draggable={false}
    />
  );
}
